import React from "react";
import cn from "classnames";
import "./index.css";
import BgTransformer from "layout/BgTransformer";
import Textarea from "layout/Textarea/Textarea";
import ContentEditable from "layout/ContentEditable/ContentEditable";
import ImageUpload from "layout/ImageUpload";
import {useSetting} from "contexts/setting";
import {useEdits} from "contexts/edits";
import {useBgUrl} from "hooks/useBgUrl";
import {useLogoUrl} from "hooks/useLogoUrl";
import {getTemplateInstanceMetadata} from "data/templateManifest";
import {MODUL_FONT_SET} from "data/products/index";
import {PLACEHOLDERS} from "data/constants";
import {isAirStackedCanvas} from "libs/airDesign";
import AirLogoMark from "./AirLogoMark";
import {toModulCase} from "libs/modulCase";

/**
 * The JetBrains Air banner skin, shared by every Releases template Air offers.
 *
 * Geometry comes from the `Release: JetBrains Air` page of the CoverBaker
 * Templates Figma file. The frames do not scale type with the canvas: the
 * headline is 72/80 and the subtitle 40/48 on every canvas from the 1300x192
 * email strip to the 1080x1920 story, so the CSS states those sizes once and
 * only the arrangement changes with the canvas shape (`isAirStackedCanvas`):
 * wide canvases put the JetBrains logo top-left and the text bottom-left; the
 * square and tall ones put the artwork on top and stack text over logo at the
 * bottom.
 *
 * Two axes pick the design:
 *
 * - `variantOption`, the brand treatment the toolbar's Variant control sets
 *   (see `variants` in data/products/jetbrains-air.js): 1 sets the headline in
 *   JetBrains Modul; 2 puts the lowercase "air" wordmark in Modul over a
 *   JetBrains Sans headline; 3 adds the Air logo mark — beside the JetBrains
 *   logo in the header on a wide canvas, over a JetBrains Sans headline on a
 *   square or tall one.
 * - `copy`, which the calling template passes: what sits under the headline.
 *   The Social Media set follows the channel's layout numbers (a subtitle on 3
 *   and 4, a screenshot upload on 5 and 6); What's New shows the feature
 *   list; the emails centre a headline over a one-line badge.
 *
 * The tall frames pin the headline's own y, so the third treatment cannot put
 * the mark on the canvas's top padding the way the square's frame does: their
 * artwork fills the top. The mark takes the headline's pinned y instead and the
 * headline follows at the square's own distance — see index.css.
 *
 * The two arrangements also answer differently to a growing title, as their
 * frames do. On a wide canvas the flex `space-between` in index.css holds the
 * text block a fixed distance off the bottom, so its TOP rises as the title
 * gains lines; when the free space runs out `space-between` degenerates to
 * `flex-start` and the block stops rising instead of climbing onto the logo.
 * The tall frames instead pin the headline's own y — a fixed 724px content
 * block on the bottom padding, the title 210px inside it and top-aligned — so
 * there the first line stays put and further lines grow DOWNWARD into the
 * space above the logo. The square has no room for that and keeps the wide
 * behaviour. tests/air-hero-anchor.spec.cjs pins all three.
 *
 * The background is a catalog-only image: it has no legacy Kraken tile behind
 * it, which is what `/scene/:id` — and therefore the server export — resolves
 * backgrounds from first. `deriveBgUrl` falls back to the catalog when the
 * tiles have nothing for the product (issue #698), so the saved scene and the
 * export get the artwork and its markers; Air's black wordmark is nonetheless
 * a product trait (the `jb-black` logo family) rather than something the
 * background asks for, so it holds on every path.
 */

/**
 * Does this product render the Air skin?
 *
 * The skin belongs to the product, not to a channel: Air draws the same
 * banners in every channel that lists it (src/data/offering.ts).
 */
export function isAirBanner(productValue) {
  return MODUL_FONT_SET.has(productValue);
}

/** The Social Media layouts that carry a subtitle / a screenshot upload. */
const SUBTITLE_LAYOUTS = [3, 4];
const UPLOAD_LAYOUTS = [5, 6];

const AirBanner = ({id, zoom, copy = "social"}) => {
  const {product, dataChannel, layoutOption, variantOption, templateId} = useSetting();
  const {url: bgUrl} = useBgUrl();
  const {titles, subtitles, badges, features, productNames} = useEdits();
  const logoUrl = useLogoUrl(id);
  // Instance-aware: the ALL-products What's New view renders one banner per
  // product under ids such as `WhatsNewPreview-AIR`. The lookup goes through
  // the product's home channel, so a banner the entry channel does not list
  // — What's New and the emails, reached from Social — still finds its size.
  const {width, height} = getTemplateInstanceMetadata(dataChannel, templateId ?? id, product.value);
  const isStacked = isAirStackedCanvas(width, height);

  const variant = Number(variantOption) || 1;
  const isEmail = copy === "email";
  // The Blog Featured frame draws no JetBrains logo: the headline sits at the
  // top-left and the artwork (or the screenshot) fills the right.
  const hasHeader = !isEmail && (templateId ?? id) !== "BlogFeatured";
  // The third treatment on a square or tall canvas (`YouTubeCommunity /
  // layout-03`) puts the Air mark ABOVE the headline, which drops to JetBrains
  // Sans, and keeps the JetBrains logo at the bottom; on a wide canvas the mark
  // shares the header row with the logo. The square stands the mark on the
  // canvas's top padding, as its frame draws it; the tall canvases have their
  // artwork there, so index.css seats the pair in their content block instead.
  const markOnTop = variant === 3 && isStacked && !isEmail;
  const hasSubtitle = copy === "social" && SUBTITLE_LAYOUTS.includes(+layoutOption);
  const hasUpload = copy === "social" && UPLOAD_LAYOUTS.includes(+layoutOption);

  const title = titles[id] || titles.main;
  const subtitle = subtitles[id] || subtitles.main;
  const badge = badges[id] || badges.main;
  const featureText = features[id] ?? features.main ?? PLACEHOLDERS.en.features;
  // The wordmark IS the product name — "air" in the frame — so it rides the
  // existing product-name slot rather than a new field: editable, saved and
  // localized like every other text on a banner.
  const heroName =
    productNames[id] || productNames.main || product.shortLabel || product.label;

  // The headline face follows the variant: Modul unless the wordmark carries
  // the brand, in which case the headline drops to JetBrains Sans.
  const titleFace = variant === 2 || markOnTop ? "air-sans" : "air-modul";

  return (
    <div
      id={id}
      className={cn("template", product.value, "air-template", `layout-${layoutOption}`, {
        "air-template--upload": hasUpload,
        "air-template--email": isEmail,
        "air-template--stacked": isStacked && !isEmail,
        "air-template--mark-top": markOnTop,
      })}
      // The host templates size their root through id-and-class selectors the
      // skin does not carry (`#WhatsNewPreview.whats-new-preview.template`,
      // `#Email01.release_email01.template`), so the canvas size comes straight
      // from the manifest — the same record the render surface reads.
      style={{"--zoom-level": zoom, width, height}}
    >
      <div>
        <BgTransformer src={bgUrl} id={id} />
        <div className={cn("air", `air-variant-${variant}`)}>
          {hasHeader && (
            <header className="template-header">
              <div className="logo">
                <img src={logoUrl} alt="JetBrains" />
              </div>
              {variant === 3 && !markOnTop && <AirLogoMark className="air-mark" />}
            </header>
          )}
          {markOnTop && <AirLogoMark className="air-mark air-mark--top" />}
          <div className="air-text">
            {variant === 2 && (
              <div className="product-name air-hero air-modul">
                <Textarea id={id} text={heroName} normalize={toModulCase} />
              </div>
            )}
            {/* Modul is lowercase-only (libs/modulCase.ts): the CSS folds what
                is drawn, the normaliser folds what is stored. */}
            <h1 className={cn("title", titleFace)}>
              <Textarea
                id={id}
                text={title}
                normalize={titleFace === "air-modul" ? toModulCase : undefined}
              />
            </h1>
            {hasSubtitle && (
              <h2 className="subtitle air-sans">
                <Textarea id={id} text={subtitle} />
              </h2>
            )}
            {copy === "features" && (
              <div className="features air-sans">
                <ContentEditable id={id} text={featureText} isList={true} />
              </div>
            )}
            {isEmail && (
              <div className="badge air-sans">
                <Textarea id={id} text={badge} inline={true} />
              </div>
            )}
          </div>
        </div>
        {hasUpload && (
          <div className="air-upload">
            <ImageUpload id={id} alwaysVisible={true} />
          </div>
        )}
      </div>
    </div>
  );
};

export default AirBanner;
